import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { ThreadDetail, ThreadListItem } from "../../shared/types";

export function SpamReminder({ waitingCount, onScreen, onRemindLater, onSkip }: { waitingCount: number; onScreen: () => void; onRemindLater: () => void; onSkip: () => void }) {
  return (
    <div className="gm-modal-scrim" style={{ zIndex: 62 }}>
      <div className="gm-modal gm-in" style={{ width: 420, marginTop: "16vh", padding: 22.4, display: "flex", flexDirection: "column", gap: 11.2 }}>
        <div className="section-header">Scheduled routine</div>
        <div style={{ fontSize: 15, fontWeight: 500 }}>Time to go through spam</div>
        <div style={{ fontSize: 12.5, color: "var(--gm-text-3)" }}>
          {waitingCount} message{waitingCount === 1 ? "" : "s"} waiting for review.
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
          <button className="btn btn-primary" onClick={onScreen}>Go through it now</button>
          <button className="btn" onClick={onRemindLater}>Remind me in an hour</button>
          <button className="btn btn-ghost" onClick={onSkip}>Skip</button>
        </div>
      </div>
    </div>
  );
}

export function SpamScreeningOverlay({ mailboxId, queue, onDone }: { mailboxId: string; queue: ThreadListItem[]; onDone: () => void }) {
  const [idx, setIdx] = useState(0);
  const [detail, setDetail] = useState<ThreadDetail | null>(null);
  const [stats, setStats] = useState({ deleted: 0, restored: 0, skipped: 0 });
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (idx >= queue.length) return;
    setDetail(null);
    api.get<ThreadDetail>(`/api/mailboxes/${mailboxId}/threads/${queue[idx]!.id}`).then(setDetail);
  }, [idx, mailboxId, queue]);

  async function act(action: "delete_permanently" | "not_spam" | "skip") {
    const current = queue[idx];
    if (current && action !== "skip") {
      await api.post(`/api/mailboxes/${mailboxId}/threads/${current.id}/action`, {
        action,
        blockSender: action === "delete_permanently",
      });
    }
    setStats((s) => ({
      ...s,
      deleted: s.deleted + (action === "delete_permanently" ? 1 : 0),
      restored: s.restored + (action === "not_spam" ? 1 : 0),
      skipped: s.skipped + (action === "skip" ? 1 : 0),
    }));
    if (idx + 1 >= queue.length) setFinished(true);
    else setIdx(idx + 1);
  }

  return (
    <div className="gm-modal-scrim" style={{ zIndex: 64 }}>
      <div className="gm-modal gm-in" style={{ width: 620, maxHeight: "86vh", display: "flex", flexDirection: "column" }}>
        <div className="gm-settings-header">
          <div style={{ fontSize: 15, fontWeight: 500 }}>Screening spam</div>
          {!finished && <span className="chip chip-neutral" style={{ marginLeft: "auto" }}>{idx + 1} of {queue.length}</span>}
        </div>
        <div style={{ padding: 22.4, overflowY: "auto", flex: 1 }}>
          {finished ? (
            <div style={{ fontSize: 13.5 }}>
              Screened {queue.length} — {stats.deleted} deleted, {stats.restored} back in Inbox
              {stats.skipped > 0 && `, ${stats.skipped} skipped`}.
            </div>
          ) : detail ? (
            <>
              <div style={{ fontSize: 12.5, fontWeight: 500 }}>{detail.from}</div>
              <div style={{ fontSize: 11, color: "var(--gm-text-4)", marginBottom: 8 }}>{new Date(detail.timeIso).toLocaleString()}</div>
              <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 8 }}>{detail.subject || "(no subject)"}</div>
              {detail.spamReason && (
                <div className="gm-summary-card">
                  <div className="section-header" style={{ marginBottom: 4 }}>Why flagged</div>
                  <div style={{ fontSize: 12.5 }}>{detail.spamReason}</div>
                </div>
              )}
              <div style={{ fontSize: 12.5, color: "var(--gm-text-3)", whiteSpace: "pre-wrap" }}>
                {detail.messages[detail.messages.length - 1]?.bodyText}
              </div>
            </>
          ) : (
            <div style={{ color: "var(--gm-text-4)" }}>Loading…</div>
          )}
        </div>
        <div style={{ padding: 14, borderTop: "1px solid var(--gm-line-soft)", display: "flex", gap: 8 }}>
          {finished ? (
            <button className="btn btn-primary" onClick={onDone}>Done</button>
          ) : (
            <>
              <button className="btn btn-primary" disabled={!detail} onClick={() => act("delete_permanently")}>Delete permanently</button>
              <button className="btn" disabled={!detail} onClick={() => act("not_spam")}>Not spam</button>
              <button className="btn btn-ghost" onClick={() => act("skip")}>Skip</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
